import { Link } from 'react-router-dom';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { ROUTES, PLAN_CONFIGS } from '@traction/shared';
import type { FeatureGate } from '@/contexts/SubscriptionContext';
import { useSubscription } from '@/contexts/SubscriptionContext';

interface UpgradePromptProps {
  reason: 'subscription_expired' | 'feature_locked' | 'limit_reached';
  feature?: FeatureGate;
  compact?: boolean;
}

const FEATURE_NAMES: Record<FeatureGate, string> = {
  lead_generation: 'Lead Generation',
  email_drafting: 'AI Email Drafting',
  gmail_integration: 'Gmail Integration',
  reply_detection: 'Reply Detection',
  priority_alerts: 'Priority Alerts',
  csv_upload: 'CSV Contact Upload',
  custom_integrations: 'Custom Integrations',
};

const FEATURE_UPGRADE_TIERS: Record<FeatureGate, string> = {
  lead_generation: 'any paid plan',
  email_drafting: 'any paid plan',
  gmail_integration: 'any paid plan',
  reply_detection: 'Starter',
  priority_alerts: 'Growth',
  csv_upload: 'Starter',
  custom_integrations: 'Scale',
};

export function UpgradePrompt({ reason, feature, compact = false }: UpgradePromptProps) {
  const { tier } = useSubscription();

  const featureName = feature ? FEATURE_NAMES[feature] : 'this feature';
  const requiredTier = feature ? FEATURE_UPGRADE_TIERS[feature] : 'a paid plan';

  if (compact) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-cacao-gold/30 bg-cacao-gold/10 px-3 py-2">
        <Lock className="h-4 w-4 text-cacao-gold" />
        <span className="text-xs text-cacao-gold">
          Upgrade to {requiredTier} to unlock
        </span>
        <Link
          to={ROUTES.SETTINGS_SUBSCRIPTION}
          className="ml-auto text-xs font-medium text-cacao-gold hover:text-cacao-gold-hover transition-colors"
        >
          Upgrade
        </Link>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-cacao-gold/20">
        {reason === 'subscription_expired' ? (
          <Lock className="h-6 w-6 text-cacao-gold" />
        ) : (
          <Sparkles className="h-6 w-6 text-cacao-gold" />
        )}
      </div>

      <h3 className="mt-4 font-heading text-lg font-medium text-[#F5F5F5]">
        {reason === 'subscription_expired'
          ? 'Subscription Required'
          : reason === 'limit_reached'
          ? 'Daily Limit Reached'
          : `Unlock ${featureName}`}
      </h3>

      <p className="mt-2 text-sm text-cacao-muted">
        {reason === 'subscription_expired' ? (
          'Your subscription has expired. Renew to continue using Traction Bee.'
        ) : reason === 'limit_reached' ? (
          <>
            You&apos;ve reached your daily limit of {PLAN_CONFIGS[tier].leads_per_day} leads.
            Upgrade to get more leads per day.
          </>
        ) : (
          <>
            {featureName} is available on {requiredTier} and above.
            Upgrade your plan to access this feature.
          </>
        )}
      </p>

      <div className="mt-6 flex justify-center gap-3">
        <Link
          to={ROUTES.SETTINGS_SUBSCRIPTION}
          className="inline-flex items-center gap-2 rounded-md bg-cacao-gold px-4 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
        >
          View Plans
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {tier === 'trial' && (
        <p className="mt-4 text-xs text-cacao-text-tertiary">
          Currently on Free Trial
        </p>
      )}
    </div>
  );
}
